/**
 * Hit testing utilities for battlemap drawings
 * Handles point-in-shape detection for different shape types
 */

import type { DrawingPath, DrawingShape } from "@/features/battlemap/types";

import { getDrawingBounds, pointInBounds } from "./geometry";

/**
 * Check if a point is inside a rectangle
 */
export function pointInRect(
  x: number,
  y: number,
  rectX: number,
  rectY: number,
  width: number,
  height: number
): boolean {
  return x >= rectX && x <= rectX + width && y >= rectY && y <= rectY + height;
}

/**
 * Check if a point is inside an ellipse
 */
export function pointInEllipse(
  x: number,
  y: number,
  rectX: number,
  rectY: number,
  width: number,
  height: number
): boolean {
  const rx = width / 2;
  const ry = height / 2;
  const cx = rectX + rx;
  const cy = rectY + ry;
  const normalizedX = (x - cx) / rx;
  const normalizedY = (y - cy) / ry;
  return normalizedX * normalizedX + normalizedY * normalizedY <= 1;
}

/**
 * Check if a point is inside a drawing shape (basic hit test)
 * Uses bounding box for polygon/brush, exact tests for rect/ellipse/text
 */
export function pointInDrawing(
  x: number,
  y: number,
  drawing: DrawingPath | DrawingShape
): boolean {
  const bounds = getDrawingBounds(drawing);

  if (drawing.type === "rect") {
    return pointInRect(
      x,
      y,
      drawing.x,
      drawing.y,
      drawing.width,
      drawing.height
    );
  }

  if (drawing.type === "ellipse") {
    return pointInEllipse(
      x,
      y,
      drawing.x,
      drawing.y,
      drawing.width,
      drawing.height
    );
  }

  if (drawing.type === "text") {
    return pointInBounds(x, y, bounds);
  }

  if (drawing.type === "polygon" || drawing.type === "brush") {
    return pointInBounds(x, y, bounds);
  }

  return false;
}

/**
 * Find the top-most drawing that contains a point
 * Returns null if no drawing is hit
 */
export function hitTestDrawings(
  x: number,
  y: number,
  drawings: (DrawingPath | DrawingShape)[],
  options?: {
    /** Filter by layer */
    layerId?: string;
  }
): string | null {
  // Iterate in reverse to find top-most element
  for (let i = drawings.length - 1; i >= 0; i--) {
    const drawing = drawings[i];

    // Filter by layer if specified
    if (options?.layerId && drawing.layer !== options.layerId) {
      continue;
    }

    if (pointInDrawing(x, y, drawing)) {
      return drawing.id;
    }
  }

  return null;
}

/**
 * Find all drawings that intersect with a bounding box (marquee selection)
 */
export function marqueeSelect(
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  drawings: (DrawingPath | DrawingShape)[]
): string[] {
  const hits: string[] = [];

  for (const drawing of drawings) {
    const drawingBounds = getDrawingBounds(drawing);

    // AABB intersection check
    if (
      bounds.minX < drawingBounds.maxX &&
      bounds.maxX > drawingBounds.minX &&
      bounds.minY < drawingBounds.maxY &&
      bounds.maxY > drawingBounds.minY
    ) {
      hits.push(drawing.id);
    }
  }

  return hits;
}

/**
 * Check if a point is within a handle area
 */
export function pointInHandle(
  x: number,
  y: number,
  handleX: number,
  handleY: number,
  handleSize: number
): boolean {
  return (
    Math.abs(x - handleX) < handleSize && Math.abs(y - handleY) < handleSize
  );
}
